// client/src/pages/ResetPassword.js
// Reset password page with token validation

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, CheckCircle, AlertCircle } from 'lucide-react';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import { authAPI } from '../services/api';

const ResetPassword = () => {
  const { t } = useTranslation(['auth']);
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');

  // Verify token on mount
  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await authAPI.verifyResetToken(token);
      if (response.data.valid) {
        setTokenValid(true);
        setEmail(response.data.email);
      } else {
        setTokenValid(false);
        setError(response.data.message);
      }
    } catch (err) {
      setTokenValid(false);
      setError(
        err.response?.data?.message || 
        t('auth:resetPassword.invalidTokenMessage')
      );
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (password.length < 8) {
      setError(t('auth:resetPassword.errors.passwordLength'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth:resetPassword.errors.passwordMismatch'));
      return;
    }

    setLoading(true);

    try {
      await authAPI.resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(
        err.response?.data?.message || 
        err.response?.data?.errors?.[0]?.msg ||
        t('auth:resetPassword.errors.resetFailed')
      );
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (success) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full">
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('auth:resetPassword.success')}
              </h2>
              
              <p className="text-gray-600 mb-6">
                {t('auth:resetPassword.successMessage')}
              </p>

              <button
                onClick={() => navigate('/login')}
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                {t('auth:resetPassword.goToLogin')}
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Token verification loading
  if (verifying) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full">
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">
                {t('auth:resetPassword.verifying')}
              </p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full">
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-10 h-10 text-red-600" />
                </div>
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('auth:resetPassword.invalidToken')}
              </h2>
              
              <p className="text-gray-600 mb-6">
                {error || t('auth:resetPassword.invalidTokenMessage')}
              </p>

              <button
                onClick={() => navigate('/forgot-password')}
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                {t('auth:resetPassword.requestNewLink')}
              </button>

              <button
                onClick={() => navigate('/login')}
                className="w-full mt-3 text-gray-600 hover:text-gray-900 font-medium"
              >
                {t('auth:forgotPassword.backToLogin')}
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Reset password form
  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-md p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <Lock className="w-8 h-8 text-blue-600" />
                </div>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {t('auth:resetPassword.title')}
              </h2>
              <p className="text-gray-600">
                {t('auth:resetPassword.subtitle')} {email}
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth:resetPassword.newPassword')}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('auth:resetPassword.newPasswordPlaceholder')}
                  disabled={loading}
                />
                <p className="mt-1 text-sm text-gray-500">
                  {t('auth:resetPassword.passwordRequirements')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth:resetPassword.confirmPassword')}
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('auth:resetPassword.confirmPasswordPlaceholder')}
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span>{t('auth:resetPassword.resetting')}</span>
                ) : (
                  <span>{t('auth:resetPassword.resetButton')}</span>
                )}
              </button>
            </form>
          </div>

          {/* Security Tips */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>{t('auth:resetPassword.passwordTips')}</strong>
            </p>
            <ul className="mt-2 text-sm text-blue-700 space-y-1 list-disc list-inside">
              <li>{t('auth:resetPassword.tip1')}</li>
              <li>{t('auth:resetPassword.tip2')}</li>
              <li>{t('auth:resetPassword.tip3')}</li>
              <li>{t('auth:resetPassword.tip4')}</li>
            </ul>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ResetPassword;